import React, { useEffect, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { stagger, useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { getBrandColorHex } from "theme/colors";
import { type ImageProps } from "next/image";
import { AnimatedStatCard, type AnimatedStatCardProps } from "./animated-stat-card";
import { PhotoBlock } from "./photo-block";

type CaseStudyStat = Pick<AnimatedStatCardProps, "customer" | "percentValue" | "description">;

type AdvertisingHeroProps = {
  primaryCaseStudyStat: CaseStudyStat;
  secondaryCaseStudyStat: CaseStudyStat;
  bgPhoto: Pick<ImageProps, "src" | "alt">;
};

const green300 = getBrandColorHex("green", 300);

function useHeroAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                [".hero-stat-card", { opacity: 1 }, { at: "+0.5", duration: 0.3, bounce: 0.7, delay: stagger(0.5) }],
                [
                  ".hero-stat-card",
                  { scale: 1 },
                  { at: "-1", type: "spring", duration: 1, bounce: 0.7, delay: stagger(0.5) },
                ],
                [
                  ".hero-stat-arrow-up",
                  { color: green300, y: "0px" },
                  { at: "-1", type: "spring", duration: 0.5, delay: stagger(0.5) },
                ],
              ]
            : [],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function CustomerStatsHeroAnimation({
  primaryCaseStudyStat,
  secondaryCaseStudyStat,
  bgPhoto,
}: AdvertisingHeroProps) {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useHeroAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.9, once: true });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex
      ref={scope}
      pos="relative"
      justify="center"
      boxSize="100%"
      maxW={{ base: "550px", lg: "100%", xl: "500px" }}
      mb={{ sm: "big", lg: 0 }}
      mt={{ base: 0, lg: "-4%" }}
    >
      <PhotoBlock {...bgPhoto} />

      <AnimatedStatCard bottom={{ base: "10%", sm: "15%" }} left="0px" {...primaryCaseStudyStat} />

      <AnimatedStatCard
        bottom="-12%"
        right={{ base: "0", xl: "-12%" }}
        display={{ base: "none", sm: "block" }}
        {...secondaryCaseStudyStat}
      />
    </Flex>
  );
}
